import { AnalysisStatus } from "../enums/status";
import { AnalysisStep } from "../enums/step";
import { AnalysisSubStep } from "../enums/subStep";
import { AnalysisStepBusinessMetricDTO } from "./steps/businessMetric";
import {
  AnalysisFeatureDTO,
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "./steps/feature";
import { AnalysisSummaryDTO, ProductRecommendationDTO } from "./steps/summary";

import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import { RootState } from "@redux/store";
import { AnalysisDecisionStatus } from "../enums/feature/decisionStatus";
import { FeatureAnalysisCollapseStateFilterLevel } from "../enums/feature/featureAnalysisCollapseLevelFilter";
import { FeeInvestmentMenuOption } from "../enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "../enums/fee/missingInvestments";
import { AnalysisStepFeeDTO } from "./steps/fee";
import { AnalysisSetupDTO } from "./steps/setup";
import { FeeHeatmapMode } from "../enums/fee/heatmapMode";
import { FeesDisplayStyle } from "../enums/fee/displayStyle";
import { FeesDisplayMode } from "../enums/fee/displayMode";

const { Setup } = AnalysisStep;

export class AnalysisSelectionDTO {
  isCompleted = false;
  status: AnalysisStatus = AnalysisStatus.InProgress;
  hasRecalculate = false;
}

export class AnalysisListDTO {
  id = "";
  name = "";
  familyGroupName?: string = "";
  ownerName = "";
  ownerAvatarUrl = "";
  adviserName = "";
  includeSteps = "";
  dataValidDate = new Date();
  lastModifiedDate = new Date();
  status: AnalysisStatus = AnalysisStatus.InProgress;
}

export class AnalysisDTO {
  id = "";
  configurationVersion = 0;
  currentStep: AnalysisStep = Setup;
  currentSubStep?: AnalysisSubStep;
  databaseVersion: Date = new Date();
  status: AnalysisStatus = AnalysisStatus.InProgress;
  isCompleted = false;
  isShowAnalysisScore = false;
  setup: AnalysisSetupDTO | null = new AnalysisSetupDTO();
  feature: AnalysisFeatureDTO | null = null;
  businessMetric: AnalysisStepBusinessMetricDTO | null = null;
  fee: AnalysisStepFeeDTO | null = null;
  summary: AnalysisSummaryDTO | null = null;
  isReview?: boolean = false;
}

export interface AnalysisFilterDTO {
  feeMissingInvestmentsStyle?: FeeMissingInvestmentsOption;
  investmentMenu?: FeeInvestmentMenuOption;
  feeTableViewMode?: FeeHeatmapMode;
  feesDisplayStyle?: FeesDisplayStyle;
  smaTableViewMode?: FeesDisplayMode;
  excludePerformanceInSMATable?: boolean;
  hidePortfolioCompositionSubtotals?: boolean;
  appendixFeeMissingInvestmentsStyle?: FeeMissingInvestmentsOption;
  showAnalysisScore?: boolean;
  showSubSection?: boolean;
  showSelectedFeatures?: boolean;
  showAppendixFeatureAnalysis?: boolean;
  showAppendixBusinessMetricAnalysis?: boolean;
  showAppendixFeeAnalysis?: boolean;
  hiddenSubProducts?: HiddenSubProductDTO[];
}

export interface HiddenSubProductDTO {
  id: string;
  name?: string;
  productName?: string;
  isRateCard: boolean;
  rateCardVersion: number;
}

export type AnalysisUserType = "supplier" | "adviser";

export class AnalysisDecisionDTO {
  analysisId = "";
  analysisStep: AnalysisStep = AnalysisStep.Feature;
  productId = "";
  decisionStatus: AnalysisDecisionStatus | null = null;
  comment: string | null = null;
}

export interface AnalysisVirtualizedStepProps {
  title: string;
  toolBarTitles?: {
    default: string;
    review: string;
  };
  tableTitle?: string;
  matchParentContainerWidth?: boolean;
  removeToolbar?: boolean;
  ignoreScrollBackToTopEvent?: boolean;
  allowFilters: boolean;
  showSectionTooltip?: boolean;
  showSubSectionTooltip?: boolean;
  containerHeight?: number | string;
  getSelectedProductsFunc: (
    state: RootState,
  ) => SelectedProductDTO[] | undefined;
  getSelectedSubProductsFunc: (
    state: RootState,
  ) => SelectedSubProductDTO[] | undefined;
  getCurrentDataFunc: (state: RootState) => FeatureAnalysisRowDTO[] | undefined;
  getCurrentCollapseLevelFunc: (
    state: RootState,
  ) => FeatureAnalysisCollapseStateFilterLevel;
  getCollapseLevelChangedManuallyFunc: (state: RootState) => boolean;
  getHiddenColumnIndexesMapFunc: (state: RootState) => boolean[] | undefined;
  getProductRecommendationDataFunc?: (
    state: RootState,
  ) => Record<string, Partial<ProductRecommendationDTO>> | undefined;
  shouldHideCollapseArrow: (state: RootState) => boolean;
}

export interface DisqualifyAllProductsDialogProps {
  callback: (value: boolean | Promise<boolean>) => void;
  numOfShortlistedProducts: number;
  numOfUndecidedProducts: number;
  numOfDisqualifiedProducts: number;
}
